import { defineStore } from "pinia";
import { StoreModule } from "@/constants/store.ts";
import { axiosInstance } from "@/plugins/https.ts";
import api from "@/_helpers/api.ts";
import { formatWorkspaceTree } from "@/_store/msp/adapters.ts";
import {
  type EditSubscriptionData,
  type MspWorkspaceDetails,
  type MspWorkspaceDialogItem,
  type MspWorkspaceListItem,
} from "@/_store/msp/msp.module.ts";
import {
  applyMspGlobalPolicy,
  archiveMspWorkspace,
  bulkEditWorkspaceLabels,
  convertMspWorkspace,
  editMspSubscription,
  editMspWorkspace,
  extendMspWorkspaceTrial,
  unarchiveMspWorkspace,
  updateMspSubscriptionType,
} from "@/_helpers/msp-store-actions.ts";
import { SubscriptionType, WorkspaceType } from "@/constants/workplaces.ts";
import type { MspLabel } from "@/_store/msp/msp-labels.module.ts";
import type { Subscription } from "@/_store/msp/msp.module.ts";
import type { DialogDataConfig } from "@/_store/dialogs.module.ts";
import type { DataValue, Paged } from "@/types.ts";
import { handleVirtualScrollData } from "@coronet/cui";
import type { ApplyGlobalPolicyData } from "@/_store/msp/global-policies/msp-global-policies.module.ts";
import type { BundleBreakdownRow } from "@coronet/cui";
import { buildWorkspaceNodeMap } from "@/_helpers/msp-treeview.ts";

/**
 * Represents a single node in the MSP workspace tree hierarchy.
 * Fields mspPolicyId, subscription, labels, and trialEndDate are used
 * for client-side filtering in the treeview tab (see {@link filterWorkspaceTreeByFilters}).
 */
export interface WorkspaceNode {
  workspaceId: string;
  parentWorkspaceId?: string;
  descendantCount: number;
  displayName: string;
  type: WorkspaceType;
  subscriptionType: SubscriptionType;
  mspPolicyId?: string;
  mspPolicyName?: string;
  subscription?: Subscription;
  labels?: MspLabel[];
  /** UTC epoch timestamp; used by nearExpirationOnly filter to check if trial expires within 7 days. */
  trialEndDate?: number;
  children?: WorkspaceNode[];
}

export interface MspTreeviewState {
  treeNodes: WorkspaceNode[];
  workspaceDetails: DataValue<MspWorkspaceDetails>;
  activatedWorkspaceId: string;
  workspaceDetailsChildren: DataValue<Paged<MspWorkspaceListItem>>;
  workspaceBundleStats: DataValue<BundleBreakdownRow[]>;
}

const defaultState: MspTreeviewState = {
  treeNodes: [],
  workspaceDetails: { loading: false },
  activatedWorkspaceId: "",
  workspaceDetailsChildren: { loading: false, data: { items: [], total: 0 } },
  workspaceBundleStats: { loading: false },
};

export const useMspTreeviewStore = defineStore(StoreModule.MSP_TREEVIEW, {
  state: (): MspTreeviewState => ({ ...defaultState }),
  actions: {
    async getTreeview() {
      const request = {
        ...api.mspHierarchyTree(),
      };

      try {
        const { data } = await axiosInstance.request<WorkspaceNode>(request);
        this.treeNodes = formatWorkspaceTree(data);
      } catch (error) {
        console.error(error);
      }
    },

    async loadPreviewDetails(workspaceId: string) {
      this.workspaceDetails = { loading: true };
      // Clear stale bundle stats from a previous channel selection immediately
      this.workspaceBundleStats = { loading: false };

      if (!workspaceId) {
        console.error("Workspace ID is required to load preview details");
        this.workspaceDetails = { loading: false, error: true };
        return;
      }

      // Only channel nodes have descendants — child/regular nodes skip children and bundle stats.
      // Relies on treeNodes being populated before this is called (guaranteed by component lifecycle).
      const nodeType = buildWorkspaceNodeMap(this.treeNodes).get(workspaceId)?.type;
      const isChannel = nodeType === WorkspaceType.CHANNEL;

      const detailsPromise = this.getHierarchyDetails(workspaceId);

      // Fire channel-specific calls in parallel with details to avoid a waterfall
      const extrasPromise = isChannel
        ? Promise.all([
            this.loadWorkspaceDetailsChildren(workspaceId),
            this.getHierarchyBundleStats(workspaceId),
          ])
        : Promise.resolve();

      const [details] = await Promise.all([detailsPromise, extrasPromise]);

      if (!details) {
        console.error("Failed to load workspace details");
        this.workspaceDetails = { loading: false, error: true };
        return;
      }

      this.workspaceDetails = { loading: false, data: details };
    },

    async getMspWorkspaceDetails(workspaceId: string): Promise<MspWorkspaceDetails | undefined> {
      const request = {
        ...api.mspWorkspaceDetails(workspaceId),
      };
      try {
        const { data } = await axiosInstance.request(request);
        return data;
      } catch (error) {
        console.error(error);
        return undefined;
      }
    },

    async getHierarchyDetails(workspaceId: string): Promise<MspWorkspaceDetails | undefined> {
      const request = {
        ...api.mspHierarchyTreeDetails(workspaceId),
      };
      try {
        const { data } = await axiosInstance.request(request);
        return data;
      } catch (error) {
        console.error(error);
        return undefined;
      }
    },

    async getHierarchyBundleStats(workspaceId: string) {
      this.workspaceBundleStats = { loading: true };
      const request = {
        ...api.mspHierarchyTreeBundleStats(workspaceId),
      };
      try {
        const { data } = await axiosInstance.request<BundleBreakdownRow[]>(request);
        this.workspaceBundleStats = { loading: false, data };
      } catch (error) {
        console.error("Error loading hierarchy bundle stats: ", error);
        this.workspaceBundleStats = { loading: false, error: true };
      }
    },

    async loadWorkspaceDetailsChildren(workspaceId: string, page = 0) {
      this.workspaceDetailsChildren.loading = true;
      const request = {
        ...api.mspHierarchyTreeChildren(workspaceId),
        params: { page, pageSize: 20 },
      };
      try {
        const { data } = await axiosInstance.request(request);
        this.workspaceDetailsChildren = {
          loading: false,
          data: {
            items:
              page > 0
                ? handleVirtualScrollData(
                    this.workspaceDetailsChildren.data?.items ?? [],
                    data.items,
                    "workspaceId"
                  )
                : data.items,
            total: data.total,
          },
        };
      } catch (error) {
        console.error("Error loading workspace children: ", error);
        this.workspaceDetailsChildren = { loading: false, error: true };
      }
    },

    async editWorkspace(payload: DialogDataConfig<MspWorkspaceDialogItem>) {
      try {
        await editMspWorkspace(payload.item, async () => {
          await this.getTreeview();
          await this.loadPreviewDetails(payload.item.workspaceId);
        });
      } catch (error) {
        console.error(error);
      }
    },

    async convertWorkspace(
      payload: DialogDataConfig<MspWorkspaceDialogItem & { descendantConversionOption?: string }>
    ) {
      try {
        await convertMspWorkspace(payload, async () => {
          await this.getTreeview();
          await this.loadPreviewDetails(payload.item.workspaceId);
        });
      } catch (error) {
        console.error(error);
      }
    },

    async unarchiveWorkspace(payload: DialogDataConfig<MspWorkspaceDialogItem>) {
      try {
        await unarchiveMspWorkspace(payload.item.workspaceId, async () => {
          await this.getTreeview();
          await this.loadPreviewDetails(payload.item.workspaceId);
        });
      } catch (error) {
        console.error(error);
      }
    },

    async archiveWorkspace(payload: DialogDataConfig<MspWorkspaceDialogItem>) {
      try {
        await archiveMspWorkspace(payload.item.workspaceId, async () => {
          await this.getTreeview();
          await this.loadPreviewDetails(payload.item.workspaceId);
        });
      } catch (error) {
        console.error(error);
      }
    },

    async editSubscription(payload: EditSubscriptionData) {
      try {
        await editMspSubscription(payload, async () => {
          await this.getTreeview();
          await this.loadPreviewDetails(this.activatedWorkspaceId);
        });
      } catch (error) {
        console.error(error);
      }
    },

    async bulkEditLabels(payload: { labelIds: string[]; workspaceIds: string[] }) {
      try {
        await bulkEditWorkspaceLabels(payload, async () => {
          await this.getTreeview();
          await this.loadPreviewDetails(this.activatedWorkspaceId);
        });
      } catch (e) {
        console.error(e);
      }
    },

    async stopSubscription(payload: DialogDataConfig<string[]>) {
      const data = {
        workspaceIds: payload.item,
        subscriptionType: SubscriptionType.FREEZE,
      };
      try {
        await updateMspSubscriptionType(data, async () => {
          await this.getTreeview();
          await this.loadPreviewDetails(this.activatedWorkspaceId);
        });
      } catch (error) {
        console.error(error);
      }
    },

    async startSubscription(payload: DialogDataConfig<string[]>) {
      const data = {
        workspaceIds: payload.item,
        subscriptionType: SubscriptionType.SUBSCRIPTION,
      };
      try {
        await updateMspSubscriptionType(data, async () => {
          await this.getTreeview();
          await this.loadPreviewDetails(this.activatedWorkspaceId);
        });
      } catch (error) {
        console.error(error);
      }
    },

    async extendTrial(payload: DialogDataConfig<MspWorkspaceDialogItem>) {
      try {
        await extendMspWorkspaceTrial(payload, async () => {
          await this.getTreeview();
          await this.loadPreviewDetails(payload.item.workspaceId);
        });
      } catch (error) {
        console.error(error);
      }
    },

    async applyGlobalPolicy(payload: ApplyGlobalPolicyData) {
      try {
        await applyMspGlobalPolicy(payload, async () => {
          await this.getTreeview();
          await this.loadPreviewDetails(this.activatedWorkspaceId);
        });
      } catch (e) {
        console.error(e);
      }
    },
  },
});
